import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import { getCurrentUser } from "../Services/apiClient";
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuHouse,
  LuImages,
  LuInfo,
  LuSave,
  LuSettings2,
} from "react-icons/lu";

import ImageUpload from "../components/ImageUpload.jsx";
import SectionInformation from "../components/SectionInformation";
import SectionOptionEdit from "../components/SectionOptionEdit";
import SectionEditVacation from "../components/SectionEditVacation";
import Spinner from "../components/Spinner";

import {
  getVacationById,
} from "../Services/vacationsService.jsx";

import {
  updateVacation,
} from "../Services/biensService.jsx";

const TOTAL_STEPS = 4;

const INITIAL_FORM_DATA = {
  allImages: "",
  images: [],

  type: "",
  libelle: "",
  category: "",

  surface: 0,
  chambres: 0,
  sallesBains: 0,
  lits: 0,
  maxGuests: 0,

  parking: false,
  meuble: false,
  kitchen: false,
  aC: false,
  piscine: false,
  garden: false,
  barbecue: false,
  beachAccess: false,
  workspace: false,
  washer: false,
  tv: false,
  wifi: false,
  camera: false,

  adresse: "",
  ville: "",
  delegation: "",
  codePostal: "",

  latitude: 0,
  longitude: 0,

  description: "",

  offre: false,
  prix: 0,
  prixReduit: 0,
  offreMonth: 0,
  offreWeek: 0,

  urlImages: "",
  folder: "",

  uid: "",
  email: "",
  tel: "",
  name: "",
};

const STEPS = [
  {
    id: 1,
    get label() { return uiI18n.t("ui.accommodation"); },
    icon: LuHouse,
  },
  {
    id: 2,
    get label() { return uiI18n.t("ui.information"); },
    icon: LuInfo,
  },
  {
    id: 3,
    get label() { return uiI18n.t("ui.amenities"); },
    icon: LuSettings2,
  },
  {
    id: 4,
    get label() { return uiI18n.t("ui.photos_290"); },
    icon: LuImages,
  },
];

function EditVacation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bienID } = useParams();

  const [formData, setFormData] =
    useState(INITIAL_FORM_DATA);

  const [
    currentSection,
    setCurrentSection,
  ] = useState(1);

  const [loading, setLoading] =
    useState(true);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [
    isModified,
    setIsModified,
  ] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Chargement
  |--------------------------------------------------------------------------
  */

  const loadVacation =
    useCallback(async () => {
      if (!bienID) {
        setError(
          t(
            "edit.invalid_id",
            "Identifiant de l'annonce invalide."
          )
        );

        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const listing =
          await getVacationById(
            bienID
          );

        const user = await getCurrentUser();
        if (listing?.uid !== user.uid) throw new Error(uiI18n.t("ui.you_do_not_own_this_listing"));

        if (!listing) {
          throw new Error(
            t(
              "edit.not_found",
              "Cette annonce est introuvable."
            )
          );
        }

        /*
          Certains endpoints semblent utiliser
          UrlImages et d'autres urlImages.
        */

        const imageString =
          listing.urlImages ??
          listing.UrlImages ??
          "";

        const existingImages =
          imageString
            ? String(imageString)
                .split(",")
                .map((image) =>
                  image.trim()
                )
                .filter(Boolean)
            : [];

        setFormData({
          ...INITIAL_FORM_DATA,
          ...listing,

          images:
            existingImages,

          allImages:
            existingImages.join(","),

          urlImages:
            imageString,
        });

        setIsModified(false);
      } catch (requestError) {
        console.error(
          "Erreur chargement location de vacances :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "editVacation.load_error",
              "Impossible de récupérer les informations de cette location."
            )
        );
      } finally {
        setLoading(false);
      }
    }, [bienID, t]);

  useEffect(() => {
    loadVacation();
  }, [loadVacation]);

  /*
  |--------------------------------------------------------------------------
  | setFormData surveillé
  |--------------------------------------------------------------------------
  */

  const updateFormData = (
    valueOrUpdater
  ) => {
    setFormData(
      (previousValue) => {
        const nextValue =
          typeof valueOrUpdater ===
          "function"
            ? valueOrUpdater(
                previousValue
              )
            : valueOrUpdater;

        return nextValue;
      }
    );

    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Changements formulaire
  |--------------------------------------------------------------------------
  */

  const onChange = (event) => {
    const {
      id,
      value,
      type,
      checked,
      files,
    } = event.target;

    if (files) {
      const imageFiles =
        Array.from(files);

      setFormData(
        (previousValue) => ({
          ...previousValue,

          images: [
            ...(previousValue.images ||
              []),
            ...imageFiles,
          ],
        })
      );

      setIsModified(true);
      return;
    }

    if (type === "checkbox") {
      setFormData(
        (previousValue) => ({
          ...previousValue,
          [id]: checked,
        })
      );

      setIsModified(true);
      return;
    }

    let normalizedValue = value;

    if (value === "true") {
      normalizedValue = true;
    } else if (
      value === "false"
    ) {
      normalizedValue = false;
    }

    setFormData(
      (previousValue) => ({
        ...previousValue,
        [id]: normalizedValue,
      })
    );

    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Images
  |--------------------------------------------------------------------------
  */

  const onImagesChange = (
    imageFiles
  ) => {
    const normalizedImages =
      Array.isArray(imageFiles)
        ? imageFiles
        : [];

    const existingImages =
      normalizedImages.filter(
        (image) =>
          typeof image === "string"
      );

    setFormData(
      (previousValue) => ({
        ...previousValue,

        images:
          normalizedImages,

        allImages:
          existingImages.join(","),

        urlImages:
          existingImages.join(","),
      })
    );

    setIsModified(true);
  };

  /*
  |--------------------------------------------------------------------------
  | Navigation
  |--------------------------------------------------------------------------
  */

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleContinue = (
    event
  ) => {
    event?.preventDefault();

    setCurrentSection(
      (previousValue) =>
        Math.min(
          previousValue + 1,
          TOTAL_STEPS
        )
    );

    scrollToTop();
  };

  const handlePrevious = (
    event
  ) => {
    event?.preventDefault();

    setCurrentSection(
      (previousValue) =>
        Math.max(
          previousValue - 1,
          1
        )
    );

    scrollToTop();
  };

  /*
  |--------------------------------------------------------------------------
  | Mise à jour
  |--------------------------------------------------------------------------
  */

  const updateClick = async (
    event
  ) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!isModified) {
      toast.info(
        t(
          "edit.no_changes",
          "Aucune modification détectée."
        )
      );

      return;
    }

    setSubmitting(true);

    try {
      const currentImages =
        Array.isArray(
          formData.images
        )
          ? formData.images
          : [];

      /*
        Nouveaux fichiers
      */

      const newImages =
        currentImages.filter(
          (image) =>
            image instanceof File
        );

      /*
        Images conservées côté serveur
      */

      const existingImages =
        currentImages.filter(
          (image) =>
            typeof image ===
            "string"
        );

      const updatedFormData = {
        ...formData,
        prix: formData.prix === "" ? 0 : Number(formData.prix),

        allImages:
          existingImages.join(","),

        urlImages:
          existingImages.join(","),
      };

      const result =
        await updateVacation(
          updatedFormData,
          newImages
        );

      toast.success(
        result?.Message ||
          result?.message ||
          t(
            "editVacation.success",
            "La location a été mise à jour."
          )
      );

      setIsModified(false);

      navigate("/myAds");
    } catch (requestError) {
      console.error(
        "Erreur mise à jour location :",
        requestError
      );

      toast.error(
        requestError?.message ||
          t(
            "editVacation.update_error",
            "Impossible de mettre à jour la location."
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Progression
  |--------------------------------------------------------------------------
  */

  const progressPercentage =
    ((currentSection - 1) /
      (TOTAL_STEPS - 1)) *
    100;

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Erreur
  |--------------------------------------------------------------------------
  */

  if (error) {
    return (
      <main className="min-h-[60vh] bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h1 className="text-xl font-black text-red-700">
            {t(
              "editVacation.error_title",
              "Impossible de modifier cette location"
            )}
          </h1>

          <p className="mt-3 text-sm leading-6 text-red-600">
            {error}
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/myAds")
            }
            className="
              mt-6
              inline-flex
              h-11
              items-center
              justify-center
              rounded-xl
              bg-slate-950
              px-5
              text-sm
              font-bold
              text-white
              transition
              hover:bg-green-600
            "
          >
            {t(
              "edit.back_ads",
              "Retour à mes annonces"
            )}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-5xl">

        {/* Header */}

        <header className="mb-8 text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-green-600"> {uiI18n.t("ui.lina_immo_holidays")} </p>

          <h1 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
            {t(
              "update_ad",
              "Modifier la location"
            )}
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            {t(
              "editVacation.description",
              "Modifiez votre logement, ses équipements, ses informations et ses photos."
            )}
          </p>

          {isModified && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-xs font-bold text-amber-700">
              <span className="h-2 w-2 rounded-full bg-amber-500" />

              {t(
                "edit.unsaved_changes",
                "Modifications non enregistrées"
              )}
            </div>
          )}
        </header>

        {/* Progression */}

        <div className="mb-7 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm sm:p-6">
          <div className="relative">

            <div className="absolute start-0 end-0 top-5 hidden h-1 rounded-full bg-slate-100 sm:block">
              <div
                className="h-full rounded-full bg-green-600 transition-all duration-500"
                style={{
                  width: `${progressPercentage}%`,
                }}
              />
            </div>

            <div className="relative grid grid-cols-4 gap-3">
              {STEPS.map((step) => {
                const Icon =
                  step.icon;

                const completed =
                  step.id <
                  currentSection;

                const active =
                  step.id ===
                  currentSection;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => {
                      if (
                        step.id <=
                        currentSection
                      ) {
                        setCurrentSection(
                          step.id
                        );
                      }
                    }}
                    className="flex flex-col items-center text-center"
                  >
                    <span
                      className={`
                        relative
                        z-10
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-full
                        border-2
                        transition

                        ${
                          completed
                            ? "border-green-600 bg-green-600 text-white"
                            : active
                              ? "border-green-600 bg-white text-green-600 shadow-lg shadow-green-600/15"
                              : "border-slate-200 bg-white text-slate-400"
                        }
                      `}
                    >
                      {completed ? (
                        <LuCheck
                          size={17}
                        />
                      ) : (
                        <Icon
                          size={17}
                        />
                      )}
                    </span>

                    <span
                      className={`
                        mt-2
                        text-[10px]
                        font-bold
                        sm:text-xs

                        ${
                          active
                            ? "text-green-700"
                            : completed
                              ? "text-slate-700"
                              : "text-slate-400"
                        }
                      `}
                    >
                      {t(
                        `editVacation.steps.${step.id}`,
                        step.label
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form */}

        <form
          onSubmit={updateClick}
          className="
            rounded-3xl
            border
            border-slate-100
            bg-white
            p-5
            shadow-xl
            shadow-slate-900/5
            sm:p-8
          "
        >

          {/* Étape 1 */}

          {currentSection === 1 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 1 })}
              title={t(
                "editVacation.property_title",
                "Informations du logement"
              )}
              description={t(
                "editVacation.property_description",
                "Modifiez les informations principales de votre location de vacances."
              )}
            >
              <SectionEditVacation
                formData={
                  formData
                }
                setFormData={
                  updateFormData
                }
                onChange={
                  onChange
                }
              />

              <StepActions
                onNext={
                  handleContinue
                }
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 2 */}

          {currentSection === 2 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 2 })}
              title={t(
                "editVacation.additional_title",
                "Informations supplémentaires"
              )}
              description={t(
                "editVacation.additional_description",
                "Modifiez la capacité, les tarifs et les informations complémentaires du logement."
              )}
            >
              <SectionInformation optionalPrice geographicSearch
                formData={
                  formData
                }
                setFormData={
                  updateFormData
                }
                onChange={
                  onChange
                }
              />

              <StepActions
                onPrevious={
                  handlePrevious
                }
                onNext={
                  handleContinue
                }
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 3 */}

          {currentSection === 3 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 3 })}
              title={t(
                "editVacation.options_title",
                "Équipements et services"
              )}
              description={t(
                "editVacation.options_description",
                "Indiquez les équipements disponibles pour les voyageurs."
              )}
            >
              <SectionOptionEdit
                formData={
                  formData
                }
                setFormData={
                  updateFormData
                }
                onChange={
                  onChange
                }
              />

              <StepActions
                onPrevious={
                  handlePrevious
                }
                onNext={
                  handleContinue
                }
                previousLabel={t(
                  "previous",
                  "Précédent"
                )}
                nextLabel={t(
                  "Continuer",
                  "Continuer"
                )}
              />
            </StepContainer>
          )}

          {/* Étape 4 */}

          {currentSection === 4 && (
            <StepContainer
              eyebrow={t("edit.step", { number: 4 })}
              title={t(
                "editVacation.photos_title",
                "Photos du logement"
              )}
              description={t(
                "editVacation.photos_description",
                "Conservez, supprimez ou ajoutez des photos à votre location."
              )}
            >
              <div
                className="
                  rounded-3xl
                  border
                  border-dashed
                  border-slate-300
                  bg-slate-50/70
                  p-4
                  transition
                  hover:border-green-300
                  hover:bg-green-50/30
                  sm:p-6
                "
              >
                <div className="mb-5 flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-green-100 text-green-600">
                    <LuImages
                      size={21}
                    />
                  </span>

                  <div>
                    <h3 className="text-sm font-black text-slate-900">
                      {t(
                        "edit.manage_photos",
                        "Gérer les photos"
                      )}
                    </h3>

                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {t(
                        "editVacation.manage_photos_description",
                        "Gardez vos photos existantes ou ajoutez de nouvelles images pour mieux présenter votre logement."
                      )}
                    </p>
                  </div>
                </div>

                <ImageUpload
                  maxImages={12}
                  initialImages={
                    formData.urlImages
                      ? String(
                          formData.urlImages
                        )
                          .split(",")
                          .map(
                            (image) =>
                              image.trim()
                          )
                          .filter(
                            Boolean
                          )
                      : []
                  }
                  onImagesChange={
                    onImagesChange
                  }
                  folder={
                    formData.folder
                  }
                />
              </div>

              {/* Actions finales */}

              <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:justify-between">

                <button
                  type="button"
                  onClick={
                    handlePrevious
                  }
                  disabled={
                    submitting
                  }
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    px-6
                    text-sm
                    font-bold
                    text-slate-700
                    transition
                    hover:bg-slate-100
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  <LuArrowLeft
                    size={17}
                    className="rtl:rotate-180"
                  />

                  {t(
                    "previous",
                    "Précédent"
                  )}
                </button>

                <button
                  type="submit"
                  disabled={
                    submitting ||
                    !isModified
                  }
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    bg-green-600
                    px-7
                    text-sm
                    font-black
                    text-white
                    shadow-lg
                    shadow-green-600/20
                    transition-all
                    hover:-translate-y-0.5
                    hover:bg-green-700
                    disabled:cursor-not-allowed
                    disabled:bg-slate-300
                    disabled:shadow-none
                  "
                >
                  {submitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                      {t(
                        "edit.updating",
                        "Mise à jour..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuSave
                        size={17}
                      />

                      {t(
                        "update_ad",
                        "Mettre à jour la location"
                      )}
                    </>
                  )}
                </button>
              </div>
            </StepContainer>
          )}
        </form>
      </div>
    </main>
  );
}

/*
|--------------------------------------------------------------------------
| Container étape
|--------------------------------------------------------------------------
*/

function StepContainer({
  eyebrow,
  title,
  description,
  children,
}) {
  useUiTranslation();
  return (
    <section>
      <div className="mb-7">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600">
          {eyebrow}
        </p>

        <h2 className="mt-2 text-2xl font-black text-slate-950">
          {title}
        </h2>

        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          {description}
        </p>
      </div>

      {children}
    </section>
  );
}

/*
|--------------------------------------------------------------------------
| Navigation étapes
|--------------------------------------------------------------------------
*/

function StepActions({
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}) {
  useUiTranslation();
  return (
    <div
      className={`
        mt-8
        flex
        flex-col-reverse
        gap-3
        border-t
        border-slate-100
        pt-6
        sm:flex-row

        ${
          onPrevious
            ? "sm:justify-between"
            : "sm:justify-end"
        }
      `}
    >
      {onPrevious && (
        <button
          type="button"
          onClick={
            onPrevious
          }
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            border
            border-slate-200
            bg-white
            px-6
            text-sm
            font-bold
            text-slate-700
            transition
            hover:bg-slate-100
          "
        >
          <LuArrowLeft
            size={17}
            className="rtl:rotate-180"
          />

          {previousLabel}
        </button>
      )}

      {onNext && (
        <button
          type="button"
          onClick={onNext}
          className="
            inline-flex
            h-12
            items-center
            justify-center
            gap-2
            rounded-2xl
            bg-slate-950
            px-7
            text-sm
            font-black
            text-white
            shadow-lg
            transition-all
            hover:-translate-y-0.5
            hover:bg-green-600
            focus:outline-none
            focus:ring-4
            focus:ring-green-500/20
          "
        >
          {nextLabel}

          <LuArrowRight
            size={17}
            className="rtl:rotate-180"
          />
        </button>
      )}
    </div>
  );
}

export default EditVacation;